import { useEffect, useRef } from "react";
import * as ExpoLinking from "expo-linking";
import { Linking } from "react-native";

import { ROUTES } from "../shared/constants/routes";
import { ANALYTICS_EVENT } from "../analytics/types";
import { trackDeeplink } from "../analytics/analyticsUtil";

import { useUserStore } from "../store/userStore";
import { useCollectionStore } from "../store/collectionStore";
import {
  getCollectionsForUser,
  getRecommendations,
  getThinkoCollections,
} from "../services/apiService";

import { useDeeplinkStore } from "../store/deeplinkStore";
import { useInstallSourceStore } from "../store/installSourceStore";
import { navRef } from "../store/navStore";
const areParamsEqual = (
  a?: Record<string, string>,
  b?: Record<string, string>
) => JSON.stringify(a ?? {}) === JSON.stringify(b ?? {});

function captureUtmSourceFromUrl(url: string) {
  const { queryParams } = ExpoLinking.parse(url);
  const raw = queryParams?.utm_source;
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (typeof value !== "string" || !value.trim()) return;
  useInstallSourceStore.getState().setInstallSource(value);
}

function parseDeepLinkUrl(url: string) {
  const urlObj = new URL(url);

  const routeName =
    (urlObj.pathname || urlObj.hostname || "")
      .replace(/\/|%2F/gi, "")
      .toLowerCase() || ROUTES.HOME;

  const params = Object.fromEntries(urlObj.searchParams.entries());

  return { routeName, params };
}

export default function useDeepLinks(enabled = true) {
  const { user } = useUserStore();

  const { consumeUrl, pushUrl } = useDeeplinkStore();

  const {
    setSelectedCollection,
    setCollectionList,
    setReload,
  } = useCollectionStore();

  const initialHandled = useRef(false);

  const getCollections = async (collectionId: string) => {
    const collections = await getCollectionsForUser();
    const recommendedCollections = await getRecommendations(collections.length < 5 ? 5 - collections.length : 1);
    const thinkoCollections = await getThinkoCollections();
    const fullList = [...thinkoCollections, ...collections, ...recommendedCollections];
    setCollectionList(fullList);

    const selected = fullList.find(
      (c) => c.id === collectionId
    );

    if (fullList.length) {
      setSelectedCollection(selected || fullList[0]);
      setReload(false);
    }
  };

  const navigateWhenReady = async (url: string) => {
    const { routeName, params } = parseDeepLinkUrl(url);

    const tryNavigate = async () => {
      if (!navRef.isReady()) return false;

      const currentRoute = navRef.getCurrentRoute();
      const shouldNavigate =
        currentRoute?.name !== routeName ||
        !areParamsEqual(
          currentRoute?.params as Record<string, string> | undefined,
          params
        );

      if (shouldNavigate) {
        if (params?.articleId && params?.collectionId) {
          await getCollections(params.collectionId);
        }
         (navRef as any).navigate(routeName, params);
      }

      return true;
    };

    if (!(await tryNavigate())) {
      let running = false;
      const interval = setInterval(async () => {
        if (running) return;
        running = true;
        try {
          if (await tryNavigate()) clearInterval(interval);
        } catch(e) {
          console.error("DEEPLINK NAVIGATION ERR : ", e)
        } finally {
          running = false;
        }
      }, 50);
    }
  };

  const ingestUrl = (url: string, runtime = false) => {
    if (!runtime) {
      // capture only from initial app-open deep link.
      captureUtmSourceFromUrl(url);
    }
    trackDeeplink({
      event: ANALYTICS_EVENT.DEEPLINK_OPENED,
      screen_name: ROUTES.WEB,
      deeplink_url: url,
    });

    if (runtime) {
      pushUrl(url);
      navigateWhenReady(url);
    } else {
      navigateWhenReady(url);
    }
  };

  // OS Deep Links
  useEffect(() => {
    if (!enabled) return;
    Linking.getInitialURL().then((url) => {
      if (url && !initialHandled.current) {
        initialHandled.current = true;
        ingestUrl(url);
      }
    });

    const sub = Linking.addEventListener("url", ({ url }) => {
      ingestUrl(url, true);
    });

    return () => sub.remove();
  }, [enabled]);

  // Queue consumer (push notifications)
  useEffect(() => {
    if (!enabled) return;

    const flushPendingUrls = async () => {
      if (!user) return;

      let url;
      while ((url = consumeUrl())) {
        await navigateWhenReady(url);
      }
    };

    const unsub = useDeeplinkStore.subscribe(
      (s) => s.pendingUrls,
      async () => {
        await flushPendingUrls();
      }
    );
    flushPendingUrls();

    return unsub;
  }, [user, enabled]);
}